import React, {useState, useEffect} from 'react';
import classNames from "classnames";
import {
  IonButton,
  IonButtons, IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import {filterCircleOutline} from "ionicons/icons";
import {FormDefinition} from "../../lib/forms";
import {ModelForm, ModelFormField} from "../../components/ModelFormHOC";
import {useAppDispatch} from "../../lib/hooks";
import {actions} from "../../lib/app";
import omit from "lodash/omit";
import {agendaDefaultDias, agendaLimiteDias} from "../../config";
import {Tarefa} from "../../models/Tarefa";
import {DateTime} from "luxon";
import {EventoDescricao, eventoRequerProdutosUtilizados} from "../../models/Evento";
import has from "lodash/has";
import qs from "qs";
import assign from "lodash/assign";


export const formDef: FormDefinition = {
  colecao_lojas: {name: 'colecao_lojas', label: 'Lojas', type: "boolean"},
  colecao_veterinarios: {name: 'colecao_veterinarios', label: 'Veterinários', type: "boolean"},
  colecao_fazendas: {name: 'colecao_fazendas', label: 'Fazendas', type: "boolean"},
  tipo_aplicacoes: {name: 'tipo_aplicacoes', label: 'Aplicações', type: "boolean"},
  tipo_semeadura: {name: 'tipo_semeadura', label: 'Semeaduras', type: "boolean"},
  dias: {name: 'dias', label: 'Período (dias)', type: "number", required: true},
}

export interface IAgendaFiltros {
  colecao_lojas: boolean,
  colecao_veterinarios: boolean,
  colecao_fazendas: boolean,
  tipo_aplicacoes: boolean,
  tipo_semeadura: boolean,
  dias: number,
}

const defaultState: IAgendaFiltros = {
  colecao_lojas: true,
  colecao_veterinarios: true,
  colecao_fazendas: true,
  tipo_aplicacoes: false,
  tipo_semeadura: false,
  dias: agendaDefaultDias,
}

export const getDefaultFiltros = (values: Partial<IAgendaFiltros>): IAgendaFiltros => {
  return {
    ...defaultState,
    ...values,
  }
}

interface ResumoItem {
  label: string;
  value: string;
}

const getResumo = (values: IAgendaFiltros): ResumoItem[] => {
  // montar resumo
  const _resumo: ResumoItem[] = [];
  const {tipo_aplicacoes, tipo_semeadura, dias} = values;

  // tipos de clientes
  const clientes: string[] = [];
  if (values.colecao_lojas)
    clientes.push("Lojas")
  if (values.colecao_veterinarios)
    clientes.push("Veterinários")
  if (values.colecao_fazendas)
    clientes.push("Fazendas")
  const itemColecoes: ResumoItem = {
    label: 'Tipo de Cliente',
    value: clientes.join(', '),
  }
  _resumo.push(itemColecoes);

  // tipo
  const tipos: string[] = [];
  if (tipo_aplicacoes) {
    tipos.push('Aplicações');
  }
  if (tipo_semeadura) {
    tipos.push('Semeadura');
  }
  if (tipos.length) {
    _resumo.push({
      label: 'Tipo',
      value: tipos.join(', '),
    });
  }

  // days
  if (dias) {
    _resumo.push({
      label: 'Período',
      value: `${dias.toString()} dias`,
    });
  }

  return _resumo;
}

type IFiltroDataMode = 'futuro' | 'passado';


export const buildQueryStringFromAgendaFiltros = (filtros: IAgendaFiltros, keep?: any): string => {
  const query: any = {};

  const cliente = [];
  if (filtros.colecao_lojas)
    cliente.push('lojas')
  if (filtros.colecao_fazendas)
    cliente.push('fazendas')
  if (filtros.colecao_veterinarios)
    cliente.push('veterinarios')
  if (cliente.length)
    query.cliente = cliente;

  if (has(filtros, 'dias'))
    query.dias = filtros.dias;

  if (has(filtros, 'tipo_aplicacoes') && filtros.tipo_aplicacoes)
    query.tipo_aplicacoes = 1;
  if (has(filtros, 'tipo_semeadura') && filtros.tipo_semeadura)
    query.tipo_semeadura = 1;

  if (keep !== undefined) {
    assign(query, keep);
  }

  return qs.stringify(query, {arrayFormat: 'comma'});
}


export const aplicarFiltrosAgenda = (_colecao: Tarefa[], _filtros: IAgendaFiltros, dataMode:IFiltroDataMode, concluido: boolean): Tarefa[] => {
  // console.log(filtrada);
  return _colecao.filter(i => {
    // filtros de analise imediata
    // dias
    if (_filtros.dias) {
      const hoje = DateTime.now().startOf('day');
      if (dataMode === 'futuro') {
        const limite = hoje.plus({days: _filtros.dias}).endOf('day');
        if (DateTime.fromJSDate(i.data.toDate()) > limite)
          return false;
      } else if (dataMode === 'passado') {
        const ontem = DateTime.now().minus({days: 1}).endOf('day');
        const limite = ontem.minus({days: _filtros.dias}).startOf('day');

        // conluidos com data no futuro, ok
        if (concluido && i.concluido)
          return true;
        if (DateTime.fromJSDate(i.data.toDate()) < limite)
          return false;
      } else {
        throw Error(`Invalid dataMode: ${dataMode}`);
      }
    }

    // tipos
    if (_filtros.tipo_aplicacoes || _filtros.tipo_semeadura) {
      if (!eventoRequerProdutosUtilizados(i.colecao, i.tipo, i.descricao))
        return false;
      if (_filtros.tipo_aplicacoes && i.descricao === EventoDescricao.APLICACAO_ASSISTIDA)
        return true;
      else if (_filtros.tipo_semeadura && i.descricao === EventoDescricao.SEMEADURA)
        return true;

      return false;
    }

    // colecao
    if (_filtros.colecao_lojas && i.colecao === 'lojas')
      return true;
    else if (_filtros.colecao_veterinarios && i.colecao === 'veterinarios')
      return true;
    else if (_filtros.colecao_fazendas && i.colecao === 'fazendas')
      return true;

    return false;
  });
}


interface AgendaFiltrosProps {
  className?: string;
  onChange: (filtros: IAgendaFiltros) => void;
  filtros: IAgendaFiltros;
}

const AgendaFiltros: React.FC<AgendaFiltrosProps> = (props) => {
  const {className, onChange, filtros} = props;
  const dispatch = useAppDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [instance, setInstance] = useState<IAgendaFiltros>(filtros);
  const [resumo, setResumo] = useState<ResumoItem[]>(getResumo(filtros));
  const [tiposAtivo, setTiposAtivo] = useState(true);

  useEffect(() => {
    setInstance(filtros);
    setResumo(getResumo(filtros))
  }, [filtros]);

  // ajustar tipos
  useEffect(() => {
    if (filtros) {
      const {colecao_fazendas} = filtros;
      setTiposAtivo(colecao_fazendas);
    }
  }, [filtros]);

  const onUpdate = async (values: any) => {
    // remover owner
    setInstance(omit(values, 'owner') as IAgendaFiltros);
  }

  const onComplete = async () => {
    setResumo(getResumo(instance));
    setIsVisible(false);
    onChange(instance);
  }

  const onValidate = (values: any) => {
    if (!values.colecao_lojas && !values.colecao_veterinarios && !values.colecao_fazendas) {
      dispatch(actions.notifyError('Pelo menos um tipo de cliente deve ser selecionado.'));
      return false;
    }

    if (!values.dias || values.dias > agendaLimiteDias) {
      dispatch(actions.notifyError(`O período máximo de consuta é de ${agendaLimiteDias} dias apenas.`));
      return false;
    }

    return true;
  }

  const onChangeTipo = (checked: boolean) => {
    if (checked) {
      return {colecao_fazendas: true};
    }
  }

  const onChangeColecaoFazendas = (checked: boolean) => {
    setTiposAtivo(checked);
    if (!checked) {
      return {tipo_aplicacoes: false, tipo_semeadura: false};
    }
  }

  return (
    <>
      <div className={classNames("rounded bg-amber-200 p-2 mx-2", className)}>
        <div
          className=" flex gap-4 items-center cursor-pointer"
          onClick={() => setIsVisible(true)}
        >
          <div className="flex-grow flex flex-col gap-2">
            {resumo.map(item => (
              <div
                key={item.label}
                className="flex gap-2 font-condensed"
              >
                <div className="w-1/3">
                  {item.label}
                </div>
                <div className="text-right truncate flex-grow opacity-50">
                  {item.value}
                </div>
              </div>
            ))
            }
          </div>
          <div className="flex flex-col justify-center">
            <IonIcon icon={filterCircleOutline} className="text-2xl"/>
          </div>
        </div>
      </div>

      <IonModal
        isOpen={isVisible}
        onDidDismiss={() => setIsVisible(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              Filtros
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={() => setIsVisible(false)}
              >
                Fechar
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <ModelForm
            formDefinition={formDef}
            onCreate={onUpdate}
            onUpdate={onUpdate}
            onComplete={onComplete}
            gender="m"
            entityLabel="Registro"
            ownerId=""
            instance={instance}
            onValidate={onValidate}
            noSuccessMessage={true}
            submitLabel="Aplicar"
          >
            <ModelFormField className="mt-4"
              field="dias"
            />

            <div className="font-hairline mt-4">
              Tipo de cliente
            </div>
            <ModelFormField
              field="colecao_lojas"
            />
            <ModelFormField
              field="colecao_veterinarios"
            />
            <ModelFormField
              field="colecao_fazendas"
              onChange={onChangeColecaoFazendas}
            />

            <div className="font-hairline mt-4">
              Tipo de evento
            </div>
            <ModelFormField
              field="tipo_aplicacoes"
              disabled={!tiposAtivo}
              onChange={onChangeTipo}
            />
            <ModelFormField
              field="tipo_semeadura"
              disabled={!tiposAtivo}
              onChange={onChangeTipo}
            />
          </ModelForm>
        </IonContent>
      </IonModal>
    </>
  );
}

export default AgendaFiltros;


